import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import {
  useUpdateMilestoneDueDateMutation,
  useRespondToMilestoneDateProposalMutation,
} from '@/redux/marketerApi'
import {
  Check,
  Circle,
  Clock,
  CalendarClock,
  ExternalLink,
  FileText,
  Video,
  Image as ImageIcon,
  Pencil,
  X,
} from 'lucide-react'
import { parseSubmissionData } from '@adthlete/common/utils/submissionDataParser'
import { formatDateOnly, getDateOnlyDiffInDays } from '@/lib/dateOnly'
import { collabPath as buildCollabPath } from '@/lib/collabPath'

const formatCheckInRelative = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

const LIME_SUBTLE_BUTTON_CLASS =
  'border-[#C0FD72]/40 bg-[#C0FD72]/10 text-[#3F7A0A] hover:bg-[#C0FD72]/20 hover:text-[#3F7A0A]'

interface Submission {
  id: string
  version: number
  contentDescription: string | null
  contentUrls: { type: string; url: string; name?: string }[] | null
  status: 'pending_review' | 'approved' | 'revision_requested' | 'superseded'
  submittedAt: string
  metadata?: Record<string, any> | null
  submitter: {
    name: string
    avatar: string | null
  } | null
}

interface CheckIn {
  active: boolean
  response: 'on_track' | 'needs_more_time' | null
  responseAt: string | null
}

interface DateProposal {
  id: string
  proposedDueDate: string
  reason: string | null
  status: 'open' | 'accepted' | 'declined' | 'cancelled' | 'expired'
  responseNote: string | null
  createdAt: string
  respondedAt: string | null
  expiresAt: string
}

interface Milestone {
  id: string
  name: string
  slug?: string
  status: 'pending' | 'in_progress' | 'submitted' | 'revision_requested' | 'approved' | 'completed'
  dueDate: string | null
  metadata?: Record<string, any> | null
  submissions: Submission[]
  checkIn?: CheckIn | null
  dateProposal?: DateProposal | null
}

interface MilestoneSubmissionsCardProps {
  milestones: Milestone[]
  campaignId: string | undefined
  collaborationId: string | undefined
  onViewSubmission: (submissionId: string) => void
  postLinks?: string[]
}

export const MilestoneSubmissionsCard: React.FC<MilestoneSubmissionsCardProps> = ({
  milestones,
  campaignId,
  collaborationId,
  onViewSubmission,
  postLinks,
}) => {
  const navigate = useNavigate()
  const { toast } = useToast()

  const collabPath = (suffix: string) => buildCollabPath(campaignId, collaborationId!, suffix)
  const [updateMilestoneDueDate, { isLoading: isUpdatingDueDate }] =
    useUpdateMilestoneDueDateMutation()
  const [respondToProposal, { isLoading: isRespondingToProposal }] =
    useRespondToMilestoneDateProposalMutation()
  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null)
  const [draftDueDate, setDraftDueDate] = useState('')

  const handleProposalResponse = async (milestone: Milestone, accept: boolean) => {
    if (!collaborationId || !milestone.dateProposal) return
    try {
      await respondToProposal({
        collaborationId,
        milestoneId: milestone.id,
        proposalId: milestone.dateProposal.id,
        accept,
      }).unwrap()
      toast({
        title: accept ? 'Deadline updated' : 'Request declined',
        description: accept
          ? `${milestone.name} now due ${formatDate(milestone.dateProposal.proposedDueDate)}.`
          : `${milestone.name} deadline unchanged. Athlete has been notified.`,
      })
    } catch (error) {
      console.error('Failed to respond to proposal:', error)
      toast({
        title: 'Could not respond',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const formatDate = (dateString: string | null | undefined) => {
    return formatDateOnly(dateString, 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const startDueDateEdit = (milestone: Milestone) => {
    setEditingMilestoneId(milestone.id)
    setDraftDueDate(milestone.dueDate ? milestone.dueDate.split('T')[0] : '')
  }

  const cancelDueDateEdit = () => {
    setEditingMilestoneId(null)
    setDraftDueDate('')
  }

  const saveDueDate = async (milestone: Milestone) => {
    if (!collaborationId) return

    try {
      await updateMilestoneDueDate({
        collaborationId,
        milestoneId: milestone.id,
        dueDate: draftDueDate || null,
      }).unwrap()

      toast({
        title: 'Due date updated',
        description: `${milestone.name} due date has been saved.`,
      })
      cancelDueDateEdit()
    } catch (error) {
      console.error('Failed to update milestone due date:', error)
      toast({
        title: 'Update failed',
        description: 'Could not save due date. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const getMilestoneStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return <Check className="h-5 w-5 text-green-500" />
      case 'submitted':
      case 'revision_requested':
        return <Circle className="h-5 w-5 text-blue-500 fill-blue-500" />
      case 'in_progress':
        return <Circle className="h-5 w-5 text-yellow-500" />
      default:
        return <Circle className="h-5 w-5 text-gray-600" />
    }
  }

  const getMilestoneStatusBadge = (milestone: Milestone) => {
    const hasPendingReview = milestone.submissions.some((s) => s.status === 'pending_review')

    if (hasPendingReview) {
      return (
        <Badge className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30">Pending Review</Badge>
      )
    }

    if (milestone.status === 'revision_requested') {
      return (
        <Badge className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30">
          Changes Requested
        </Badge>
      )
    }

    if (milestone.status === 'approved' || milestone.status === 'completed') {
      return (
        <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/30">Approved</Badge>
      )
    }

    if (milestone.dueDate) {
      const daysUntilDue = getDateOnlyDiffInDays(milestone.dueDate)

      if (daysUntilDue === null) {
        return null
      }

      if (daysUntilDue < 0) {
        return <Badge className="bg-red-500/20 text-red-400 hover:bg-red-500/30">Overdue</Badge>
      } else if (daysUntilDue <= 2) {
        return (
          <Badge className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30">
            Due Soon
          </Badge>
        )
      }
    }

    return null
  }

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className="h-4 w-4" />
      case 'image':
        return <ImageIcon className="h-4 w-4" />
      default:
        return <FileText className="h-4 w-4" />
    }
  }

  if (milestones.length === 0) return null

  return (
    <div className="bg-card border border-foreground/10 rounded-2xl p-4 shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
      <p className="text-xs font-semibold tracking-wider text-gray-500 uppercase mb-4">
        Milestone Submissions
      </p>
      <div>
        <div className="space-y-4">
          {milestones.map((milestone) => {
            const hasPendingReview = milestone.submissions.some(
              (s) => s.status === 'pending_review',
            )
            const isCampaignLive = milestone.slug === 'collaboration_live'
            const isLicenseContent = milestone.slug === 'license_content'
            const isEditingThisMilestone = editingMilestoneId === milestone.id

            // Special case for License Content milestone — link to dedicated licensing page
            if (isLicenseContent) {
              return (
                <div
                  key={milestone.id}
                  className="border border-foreground/10 rounded-xl bg-card p-4 space-y-3 shadow-[0_1px_2px_rgba(30,41,59,0.04)]"
                >
                  <div className="flex items-start gap-3">
                    {getMilestoneStatusIcon(milestone.status)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{milestone.name}</h4>
                        {getMilestoneStatusBadge(milestone)}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        View the licensing offer details and athlete-submitted authorization codes.
                      </p>
                      <Button
                        size="sm"
                        variant="lime"
                        className="mt-3"
                        onClick={() => navigate(collabPath('/licensing'))}
                      >
                        View Licensing Details
                      </Button>
                    </div>
                  </div>
                </div>
              )
            }

            // Special case for Campaign Live milestone
            if (isCampaignLive && postLinks && postLinks.length > 0) {
              return (
                <div
                  key={milestone.id}
                  className="border border-[#C0FD72]/30 rounded-xl bg-[#C0FD72]/10 p-4 space-y-3 mb-4"
                >
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-[#3F7A0A]" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-foreground">{milestone.name}</h4>
                        <Badge className="bg-[#C0FD72]/20 text-[#3F7A0A] hover:bg-[#C0FD72]/30 border-0">
                          Completed
                        </Badge>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm text-gray-600">Due Date</p>
                        {!isEditingThisMilestone ? (
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-sm text-gray-300">
                              {formatDate(milestone.dueDate) ?? 'Not set'}
                            </p>
                            <Button
                              size="sm"
                              variant="lime"
                              className="h-7 px-2"
                              onClick={() => startDueDateEdit(milestone)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="mt-1 flex items-center gap-2">
                            <Input
                              type="date"
                              value={draftDueDate}
                              onChange={(e) => setDraftDueDate(e.target.value)}
                              className="h-8 w-44"
                            />
                            <Button
                              size="sm"
                              variant="lime"
                              className="h-8"
                              onClick={() => saveDueDate(milestone)}
                              disabled={isUpdatingDueDate}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className={`h-8 ${LIME_SUBTLE_BUTTON_CLASS}`}
                              onClick={cancelDueDateEdit}
                              disabled={isUpdatingDueDate}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-sm text-gray-700">
                        Campaign is live! The athlete has published the content.
                      </div>
                      <div className="mt-3">
                        <Link
                          to={collabPath('/collaboration-live')}
                          className="inline-flex items-center gap-2 text-sm text-[#3F7A0A] hover:text-[#b6f45f] hover:underline bg-[#C0FD72]/10 px-3 py-2 rounded-md border border-[#C0FD72]/20 transition-colors w-fit"
                        >
                          <ExternalLink className="h-4 w-4" />
                          View published posts ({postLinks.length})
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )
            }

            const latestSubmission = milestone.submissions[0]

            return (
              <div
                key={milestone.id}
                className="border border-foreground/10 rounded-xl bg-card p-4 space-y-3 shadow-[0_1px_2px_rgba(30,41,59,0.04)]"
              >
                <div className="flex items-start gap-3">
                  {getMilestoneStatusIcon(milestone.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-foreground">{milestone.name}</h4>
                      {getMilestoneStatusBadge(milestone)}
                      {milestone.checkIn?.response === 'on_track' &&
                        milestone.checkIn.responseAt && (
                          <Badge className="bg-[#C0FD72]/20 text-[#3F7A0A] hover:bg-[#C0FD72]/30 text-xs font-medium border border-[#C0FD72]/30">
                            <Check className="h-3 w-3 mr-1" />
                            Athlete on track ·{' '}
                            {formatCheckInRelative(milestone.checkIn.responseAt)}
                          </Badge>
                        )}
                    </div>
                    {milestone.checkIn?.active && (
                      <div className="mt-2 p-3 bg-card border border-foreground/15 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Clock className="h-4 w-4 text-gray-700 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                              Status check-in sent
                            </p>
                            <p className="text-xs text-gray-700 mt-0.5 leading-relaxed">
                              {milestone.dueDate
                                ? `Less than 3 days until ${formatDate(milestone.dueDate)}. `
                                : 'Deadline approaching. '}
                              We've nudged the athlete to confirm they're on track — their response
                              will appear here.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {milestone.checkIn?.response === 'on_track' &&
                      milestone.checkIn.responseAt && (
                        <div className="mt-2 p-3 bg-[#C0FD72]/10 border border-[#C0FD72]/25 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-[#3F7A0A] mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold uppercase tracking-wider text-[#3F7A0A]">
                                Athlete confirmed on track
                              </p>
                              <p className="text-xs text-gray-700 mt-0.5 leading-relaxed">
                                The athlete confirmed they're on track
                                {milestone.dueDate
                                  ? ` for ${formatDate(milestone.dueDate)}`
                                  : ''}{' '}
                                ({formatCheckInRelative(milestone.checkIn.responseAt)}). No action
                                needed — we'll let you know if anything changes.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">Due Date</p>
                      {!isEditingThisMilestone ? (
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm">
                            {formatDate(milestone.dueDate) ?? (
                              <span className="text-gray-600">Not set</span>
                            )}
                          </p>
                          <Button
                            size="sm"
                            variant="lime"
                            className="h-7 px-2"
                            onClick={() => startDueDateEdit(milestone)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="mt-1 flex items-center gap-2">
                          <Input
                            type="date"
                            value={draftDueDate}
                            onChange={(e) => setDraftDueDate(e.target.value)}
                            className="h-8 w-44"
                          />
                          <Button
                            size="sm"
                            variant="lime"
                            className="h-8"
                            onClick={() => saveDueDate(milestone)}
                            disabled={isUpdatingDueDate}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`h-8 ${LIME_SUBTLE_BUTTON_CLASS}`}
                            onClick={cancelDueDateEdit}
                            disabled={isUpdatingDueDate}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {milestone.dateProposal?.status === 'open' && (
                  <div className="ml-8 p-3 bg-[#F9DA4C]/10 border border-[#F9DA4C]/30 rounded-lg">
                    <div className="flex items-start gap-2">
                      <CalendarClock className="h-4 w-4 text-[#B45309] mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#B45309]">
                          Athlete proposed new deadline
                        </p>
                        <p className="text-sm text-foreground mt-0.5">
                          {formatDate(milestone.dueDate) ?? 'Current'}{' '}
                          <span className="text-gray-500">→</span>{' '}
                          <span className="font-medium">
                            {formatDate(milestone.dateProposal.proposedDueDate)}
                          </span>
                        </p>
                        {milestone.dateProposal.reason && (
                          <p className="text-xs text-gray-600 mt-1.5 italic">
                            "{milestone.dateProposal.reason}"
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="lime"
                            disabled={isRespondingToProposal}
                            onClick={() => handleProposalResponse(milestone, true)}
                            className="h-7 text-xs px-3"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Accept new date
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isRespondingToProposal}
                            onClick={() => handleProposalResponse(milestone, false)}
                            className="h-7 text-xs px-3 border-foreground/15 text-gray-700 bg-transparent hover:bg-foreground/5"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Decline
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submission Details */}
                <div className="ml-8 border-l-2 border-foreground/10 pl-4 space-y-2">
                  {!latestSubmission ? (
                    <p className="text-sm text-gray-600">No submissions yet for this milestone.</p>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <Avatar className="h-6 w-6">
                          {latestSubmission.submitter?.avatar && (
                            <AvatarImage src={latestSubmission.submitter.avatar} />
                          )}
                          <AvatarFallback className="text-xs">
                            {latestSubmission.submitter?.name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{latestSubmission.submitter?.name}</span>
                        <span className="text-muted-foreground">
                          submitted v{latestSubmission.version}
                        </span>
                      </div>

                      {latestSubmission.contentDescription &&
                        (() => {
                          const parsed = parseSubmissionData(latestSubmission.contentDescription)
                          if (parsed.fields.length === 0) return null

                          const firstField = parsed.fields[0]
                          const preview =
                            firstField.value.length > 150
                              ? firstField.value.substring(0, 150) + '...'
                              : firstField.value

                          return (
                            <div className="text-sm space-y-1">
                              <div className="font-medium text-muted-foreground">
                                {firstField.label}
                              </div>
                              <div className="text-foreground/80 pl-2 border-l-2 border-muted">
                                {preview}
                              </div>
                              {parsed.fields.length > 1 && (
                                <div className="text-xs text-muted-foreground">
                                  +{parsed.fields.length - 1} more field
                                  {parsed.fields.length > 2 ? 's' : ''}
                                </div>
                              )}
                            </div>
                          )
                        })()}

                      {latestSubmission.contentUrls && latestSubmission.contentUrls.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {latestSubmission.contentUrls.map((content, i) => (
                            <a
                              key={i}
                              href={content.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 bg-blue-500/10 border border-blue-500/20 px-2 py-1 rounded-md"
                            >
                              {getContentIcon(content.type)}
                              {content.name || `File ${i + 1}`}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className={LIME_SUBTLE_BUTTON_CLASS}
                          onClick={() => onViewSubmission(latestSubmission.id)}
                        >
                          View Details
                        </Button>
                        {hasPendingReview && (
                          <Button
                            size="sm"
                            variant="lime"
                            onClick={() =>
                              navigate(collabPath(`/milestone/${milestone.id}/review`))
                            }
                          >
                            Review Submission
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )
          })}

          {milestones.length === 0 && (
            <div className="text-center py-8 text-gray-600">
              <div className="flex justify-center mb-3">
                <FileText className="h-10 w-10 text-gray-400" />
              </div>
              <p className="font-medium text-foreground">No milestones yet</p>
              <p className="text-sm">Milestones will appear once this collaboration is set up.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
